import { useEffect, useMemo, useRef } from "react"

import { Spin } from "antd"
import {
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  TimeScale,
  Title,
  Tooltip
} from "chart.js"
import { Line } from "react-chartjs-2"
import { useSelector } from "react-redux"

import { debounce } from "../../../Utils/utils"
import { alertsChartOptions } from "./chartsOptions"
import { getGradient, tooltipLine } from "./chartsPlagins"

ChartJS.register(TimeScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler)

ChartJS.defaults.color = "#667085"
// ChartJS.defaults.font.family = 'FontAwesome, Inter, sans-serif';

ChartJS.defaults.font.family = "Inter, sans-serif"
ChartJS.defaults.plugins.tooltip.titleAlign = "center"
ChartJS.defaults.plugins.tooltip.padding = 12
ChartJS.defaults.plugins.tooltip.titleMarginBottom = 8
ChartJS.defaults.plugins.tooltip.titleFont = {
  fontFamily: "Inter, sans-serif",
  lineHeight: 1.2,
  size: 14,
  style: "normal",
  weight: "700"
}

const AlertsOverviewChart = ({ data, isFetching }) => {
  const { alertsOverviewData, alertsOverviewFetching, filters } = useSelector(
    (state) => state.dashboard
  )
  const theme = useSelector((state) => state.nav.theme)

  //for better resize chart if resize window
  const chartRef = useRef(null)

  const handleResize = debounce(() => {
    if (chartRef.current) {
      chartRef.current.resize()
    }
  }, 500)

  //for udate chart after changing browser tab if chart is unvisible
  const handleVisibilityChange = () => {
    if (document.visibilityState === "visible" && chartRef.current) {
      chartRef.current.update()
    }
  }

  useEffect(() => {
    window.addEventListener("resize", handleResize)
    document.addEventListener("visibilitychange", handleVisibilityChange)
    return () => {
      window.removeEventListener("resize", handleResize)
      document.removeEventListener("visibilitychange", handleVisibilityChange)
    }
  }, [])

  const chartOptions = useMemo(() => alertsChartOptions(theme), [theme])

  const getChartData = (data) => {
    const comparison = data?.comparison
    const mainLabels = data?.labels || []
    const compLabels = comparison?.labels || []

    const mainSet = new Set(mainLabels)
    const hasOverlap = compLabels.length > 0 && compLabels.some((l) => mainSet.has(l))

    const mapAtLabels = (targetLabels, srcLabels, srcArr) => {
      if (!srcArr) return srcArr
      const map = new Map(srcLabels.map((l, i) => [l, srcArr[i]]))
      return targetLabels.map((l) => (map.has(l) ? map.get(l) : null))
    }

    let labels
    let mainTotal, mainEthoca, mainRdr, mainCdrn, mainLabelsAligned
    let compTotal, compEthoca, compRdr, compCdrn, compLabelsAligned

    if (hasOverlap) {
      // align by calendar date — union of labels
      const union = Array.from(new Set([...mainLabels, ...compLabels])).sort()
      labels = union
      mainTotal = mapAtLabels(labels, mainLabels, data?.result?.data)
      mainEthoca = mapAtLabels(labels, mainLabels, data?.result?.ethoca)
      mainRdr = mapAtLabels(labels, mainLabels, data?.result?.rdr)
      mainCdrn = mapAtLabels(labels, mainLabels, data?.result?.cdrn || data?.result?.crdn)
      compTotal = mapAtLabels(labels, compLabels, comparison?.data)
      compEthoca = mapAtLabels(labels, compLabels, comparison?.ethoca)
      compRdr = mapAtLabels(labels, compLabels, comparison?.rdr)
      compCdrn = mapAtLabels(labels, compLabels, comparison?.cdrn || comparison?.crdn)
      mainLabelsAligned = labels.map((l) => (mainSet.has(l) ? l : null))
      compLabelsAligned = labels
    } else {
      // index-in-index — X-axis follows the longer series so nothing gets cut off
      const compLonger = compLabels.length > mainLabels.length
      labels = compLonger ? compLabels : mainLabels
      mainTotal = data?.result?.data
      mainEthoca = data?.result?.ethoca
      mainRdr = data?.result?.rdr
      mainCdrn = data?.result?.cdrn || data?.result?.crdn
      compTotal = comparison?.data
      compEthoca = comparison?.ethoca
      compRdr = comparison?.rdr
      compCdrn = comparison?.cdrn || comparison?.crdn
      mainLabelsAligned = labels.map((_, i) => (i < mainLabels.length ? mainLabels[i] : null))
      compLabelsAligned = labels.map((_, i) => (i < compLabels.length ? compLabels[i] : null))
    }

    const datasets = [
      {
        label: "All projects",
        data: mainTotal,

        selectType: filters.alertsType,

        total: mainTotal,
        ethoca: mainEthoca,
        rdr: mainRdr,
        cdrn: mainCdrn,

        comparisonTotal: compTotal,
        comparisonEthoca: compEthoca,
        comparisonRdr: compRdr,
        comparisonCdrn: compCdrn,
        mainLabels: mainLabelsAligned,
        comparisonLabels: compLabelsAligned,
        comparisonLabel: data?.comparison_period?.label,

        borderColor: "#225AEA",
        borderWidth: 2,
        pointHoverRadius: 8,
        pointHoverBorderWidth: 2,
        pointHoverBackgroundColor: "white",
        yAxisID: "y",
        fill: true,
        backgroundColor: function (context) {
          const chart = context.chart
          const { ctx, chartArea } = chart
          if (!chartArea) return
          return getGradient(ctx, chartArea)
        },
        ...(labels.length === 1
          ? { pointHoverRadius: 6, pointRadius: 4 }
          : { pointHoverRadius: 6, pointRadius: 0 })
      }
    ]

    if (compTotal?.length) {
      datasets.push({
        label: "Comparison",
        data: compTotal,
        borderColor: "#A6B6E8",
        borderWidth: 1.5,
        borderDash: [6, 6],
        pointRadius: 0,
        pointHoverRadius: 0,
        yAxisID: "y",
        fill: false,
        order: 2
      })
    }

    return { labels, datasets }
  }

  const chartData = getChartData(data ?? alertsOverviewData)

  return (
    <Spin spinning={isFetching ?? alertsOverviewFetching}>
      <Line
        ref={chartRef}
        options={chartOptions}
        data={chartData}
        height={300}
        plugins={[tooltipLine]}
      />
    </Spin>
  )
}

export default AlertsOverviewChart
